
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Analytics from '@/lib/mixpanel';
import { devLogger } from '@/lib/dev-logger';

export const useAnalytics = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Track page views automatically
  useEffect(() => {
    const pageName = getPageName(location.pathname);
    devLogger.log('📊 Tracking page view:', pageName);
    Analytics.trackPageView(pageName, {
      path: location.pathname,
      search: location.search,
      authenticated: !!user
    });
  }, [location.pathname, location.search, user]);

  // User identification when user changes
  useEffect(() => {
    if (user) {
      devLogger.log('👤 Identifying user:', user.id);
      Analytics.identify(user.id, {
        email: user.email,
        created_at: user.created_at,
        last_sign_in_at: user.last_sign_in_at,
        email_verified: user.email_confirmed_at ? true : false
      });
    }
  }, [user]);

  // Helper function to get page name from path
  const getPageName = (pathname: string): string => {
    switch (pathname) {
      case '/':
        return 'Landing Page';
      case '/login':
        return 'Login Page';
      case '/signup':
        return 'Sign Up Page';
      case '/forgot-password':
        return 'Forgot Password Page';
      case '/personalization':
        return 'Personalization Page';
      default:
        if (pathname.startsWith('/document/')) {
          return 'Document Page';
        }
        if (pathname.startsWith('/session/')) {
          return 'Session Page';
        }
        return 'Unknown Page';
    }
  };

  // Tracking methods with context
  const trackEvent = useCallback((eventName: string, properties?: Record<string, any>) => {
    Analytics.track(eventName, {
      ...properties,
      current_page: getPageName(location.pathname),
      user_id: user?.id,
      authenticated: !!user
    });
  }, [location.pathname, user]);

  const trackDocumentUpload = useCallback((documentInfo: {
    file_name: string;
    file_size: number;
    file_type: string;
  }) => {
    Analytics.trackDocumentUploaded(documentInfo);
  }, []);

  const trackDocumentView = useCallback((documentId: string, documentName: string) => {
    Analytics.trackDocumentViewed(documentId, documentName);
  }, []);

  const trackDocumentDelete = useCallback((documentId: string, documentName: string) => {
    Analytics.trackDocumentDeleted(documentId, documentName);
  }, []);

  const trackSessionStart = useCallback((sessionInfo: {
    session_id: string;
    document_id: string;
    document_name: string;
  }) => {
    Analytics.trackSessionStarted(sessionInfo);
  }, []);

  const trackSessionEnd = useCallback((sessionInfo: {
    session_id: string;
    duration_seconds: number;
    messages_count: number;
  }) => {
    Analytics.trackSessionEnded(sessionInfo);
  }, []);

  const trackAIInteraction = useCallback((type: 'conversation_started' | 'message_sent' | 'message_received' | 'voice_started' | 'voice_ended', sessionId: string, data?: any) => {
    switch (type) {
      case 'conversation_started':
        Analytics.trackAIConversationStarted(sessionId);
        break;
      case 'message_sent':
        Analytics.trackAIMessageSent(sessionId, data?.messageLength || 0);
        break;
      case 'message_received':
        Analytics.trackAIMessageReceived(sessionId, data?.messageLength || 0, data?.responseTime || 0);
        break;
      case 'voice_started':
        Analytics.trackVoiceInteractionStarted(sessionId);
        break;
      case 'voice_ended':
        Analytics.trackVoiceInteractionEnded(sessionId, data?.duration || 0);
        break;
    }
  }, []);

  const trackPDFInteraction = useCallback((type: 'page_changed' | 'zoom_changed', sessionId: string, data: any) => {
    switch (type) {
      case 'page_changed':
        Analytics.trackPDFPageChanged(sessionId, data.pageNumber, data.totalPages);
        break;
      case 'zoom_changed':
        Analytics.trackPDFZoomChanged(sessionId, data.zoomLevel);
        break;
    }
  }, []);

  const trackFeatureUsage = useCallback((featureName: string, context?: Record<string, any>) => {
    Analytics.trackFeatureUsed(featureName, context);
  }, []);

  const trackError = useCallback((errorType: string, errorMessage: string, context?: Record<string, any>) => {
    Analytics.trackError(errorType, errorMessage, {
      ...context,
      current_page: getPageName(location.pathname),
      user_id: user?.id
    });
  }, [location.pathname, user]);

  const trackPersonalization = useCallback((preferences: {
    age?: number;
    goals?: string;
    interaction_preferences?: string;
  }) => {
    Analytics.trackPersonalizationCompleted(preferences);
  }, []);

  const trackPerformance = useCallback((metricName: string, value: number, unit: string) => {
    Analytics.trackPerformanceMetric(metricName, value, unit);
  }, []);

  return {
    // Direct tracking methods
    trackEvent,
    trackDocumentUpload,
    trackDocumentView,
    trackDocumentDelete,
    trackSessionStart,
    trackSessionEnd,
    trackAIInteraction,
    trackPDFInteraction,
    trackFeatureUsage,
    trackError,
    trackPersonalization,
    trackPerformance,
    
    // Utility methods
    identify: Analytics.identify,
    setUserProperties: Analytics.setUserProperties,
    reset: Analytics.reset,
    
    // Current context
    currentPage: getPageName(location.pathname),
    isAuthenticated: !!user,
    userId: user?.id
  };
};

export default useAnalytics;
